import { z } from "zod";

export const companySchema = z.object({
  id: z.number().int(),
  coreSignalId: z.number().nullable().optional(),
  name: z.string(),
  linkedInUrl: z.string(),
  isActive: z.boolean(),
  creatorId: z.string().nullable().optional(),
  createdDate: z.string(),
  modifiedBy: z.string().nullable().optional(),
  modifiedDate: z.string().nullable().optional(),
});

export const createCompanyRequestSchema = z.object({
  name: z.string().min(1, "Name is required"),
  linkedInUrl: z.string().min(1, "LinkedIn URL is required"),
});

export const updateCompanyStatusRequestSchema = z.object({
  isActive: z.boolean(),
});

export const departmentCountSchema = z.object({
  department: z.string(),
  count: z.number().int(),
});

export const companyStatsSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  departments: z.array(departmentCountSchema),
});

export const departmentFilterSchema = z.object({
  name: z.string(),
  minEmployees: z.number().int(),
  maxEmployees: z.number().int(),
});

export const companyStatsWithDepartmentsSchema = z.object({
  companies: z.array(companyStatsSchema),
  departments: z.array(departmentFilterSchema),
});

export const profileSearchColumnSchema = z.enum([
  "None",
  "FullName",
  "Title",
  "Location",
  "Country",
  "CurrentCompany",
  "Seniority",
  "Department",
  "LinkedInUrl",
  "School",
]);

export const profileQueryRequestSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().default(30),
  country: z.string().optional().nullable(),
  seniority: z.string().optional().nullable(),
  department: z.string().optional().nullable(),
  minF1ExperienceYears: z.number().int().min(0).optional().nullable(),
  maxF1ExperienceYears: z.number().int().min(0).optional().nullable(),
  minTotalExperienceYears: z.number().int().min(0).optional().nullable(),
  maxTotalExperienceYears: z.number().int().min(0).optional().nullable(),
  maxFakeProbability: z.number().int().min(0).max(100).optional().nullable(),
  searchColumn: profileSearchColumnSchema.optional().nullable(),
  searchTerm: z.array(z.string().max(255)).optional().nullable(),
  sortBy: z.string().optional().nullable(),
  sortDirection: z.string().default("DESC"),
});

export const profileEducationSchema = z.object({
  institutionName: z.string().nullable().optional(),
  degree: z.string().nullable().optional(),
  dateFromYear: z.number().int().nullable().optional(),
  dateToYear: z.number().int().nullable().optional(),
  yearsOfStudy: z.number().int(),
});

export const profileCompanyExperienceSchema = z.object({
  companyName: z.string().nullable().optional(),
  dateFromYear: z.number().int().nullable().optional(),
  dateToYear: z.number().int().nullable().optional(),
});

export const profileSchema = z.object({
  id: z.number().int(),
  coreSignalId: z.number(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  fullName: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  linkedInUrl: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  experienceCount: z.number().int(),
  totalExperienceMonths: z.number().int(),
  currentCompany: z.string().nullable().optional(),
  seniority: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  f1ExperienceMonths: z.number().int().nullable().optional(),
  australiaExperienceMonths: z.number().int().nullable().optional(),
  usaExperienceMonths: z.number().int().nullable().optional(),
  hasF1V8Experience: z.boolean(),
  hasFiaExperience: z.boolean(),
  hasAusEducation: z.boolean(),
  hasUsEducation: z.boolean(),
  pumExperienceMonths: z.number().int().nullable().optional(),
  education: z.array(profileEducationSchema).optional().nullable(),
  companiesWorkedAt: z.array(profileCompanyExperienceSchema).optional().nullable(),
  fakeProbability: z.number().int().min(0).max(100).nullable().optional(),
  lastSyncedAt: z.string(),
  lastClassifiedAt: z.string().nullable().optional(),
});

export const profileQueryResponseSchema = z.object({
  totalCount: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  totalPages: z.number().int(),
  items: z.array(profileSchema),
});

export const profileFiltersResponseSchema = z.object({
  countries: z.array(z.string()),
  seniorities: z.array(z.string()),
  departments: z.array(z.string()),
});
